/** @jsxImportSource @emotion/react */
import React, { useEffect, useRef } from 'react'
import css from '@emotion/css/macro'
import { InputRef } from 'antd'
import Text from 'src/components/ts-components/Text'
import { TextInput } from 'src/components/ts-components/TextInput'
import ModernPhoneInput, {
  ModernPhoneInputRef,
} from 'src/components/ts-components/ModernPhoneInput'
import colors from '@sportninja/common/constants/appColors'
import { useTeamContext } from '../../TeamContext'

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
const websiteRegex =
  /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/

const FIELD_ORDER = [
  'phone',
  'email',
  'website',
  'support_phone',
  'support_email',
  'support_website',
] as const

interface Props {
  isMobile?: boolean
}

const ContactInfo = ({ isMobile }: Props) => {
  const { state, dispatch, setCustomErrorByField, clearCustomErrorByField } =
    useTeamContext()

  const phoneRef = useRef<ModernPhoneInputRef>(null)
  const emailRef = useRef<InputRef>(null)
  const websiteRef = useRef<InputRef>(null)
  const supportPhoneRef = useRef<ModernPhoneInputRef>(null)
  const supportEmailRef = useRef<InputRef>(null)
  const supportWebsiteRef = useRef<InputRef>(null)

  useEffect(() => {
    if (state.fieldErrors.length > 0) {
      const fieldRefs: Record<
        string,
        React.RefObject<{ focus?: () => void } | null>
      > = {
        phone: phoneRef,
        email: emailRef,
        website: websiteRef,
        support_phone: supportPhoneRef,
        support_email: supportEmailRef,
        support_website: supportWebsiteRef,
      }
      const firstErrorField = FIELD_ORDER.find((field) =>
        state.fieldErrors.includes(field)
      )
      if (firstErrorField) {
        fieldRefs[firstErrorField]?.current?.focus?.()
      }
    }
  }, [state.fieldErrors])

  return (
    <div
      css={css`
        display: flex;
        padding: 16px;
        gap: 16px;
        flex-direction: column;
        align-items: flex-end;
        overflow-y: auto;
        height: 100%;
        ${isMobile &&
        css`
          padding-left: 8px;
          padding-right: 8px;
        `}
      `}
    >
      {/* Contact Info Header */}
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <Text variant='captionLarge' weight='semiBold'>
          Contact Info
        </Text>
      </div>

      {/* Phone + Email row */}
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          align-self: stretch;
          flex-direction: row;
          gap: 16px;
          ${isMobile &&
          css`
            flex-direction: column;
          `}
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            flex: 1 0 0;
            ${isMobile &&
            css`
              width: 100%;
            `}
          `}
        >
          <ModernPhoneInput
            ref={phoneRef}
            value={state.phone}
            onChange={(value) => {
              dispatch({ type: 'SET_FIELD', field: 'phone', value })
            }}
            onCountryChange={(country) => {
              dispatch({
                type: 'SET_FIELD',
                field: 'phone_country',
                value: country || '',
              })
            }}
            label='Phone Number'
            error={
              state.fieldErrors.includes('phone') ? 'Invalid phone number' : ''
            }
            validateOnBlur={false}
          />
        </div>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            flex: 1 0 0;
            ${isMobile &&
            css`
              width: 100%;
            `}
          `}
        >
          <TextInput
            ref={emailRef}
            label='Email'
            value={state.email}
            setValue={(value) => {
              dispatch({ type: 'SET_FIELD', field: 'email', value })
            }}
            onBlur={() => {
              if (state.email.length > 0 && !emailRegex.test(state.email)) {
                setCustomErrorByField('email', 'Invalid email')
              } else {
                clearCustomErrorByField('email')
              }
            }}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            name='email'
            type='email'
          />
        </div>
      </div>

      {/* Website */}
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          align-self: stretch;
          flex-direction: row;
          gap: 16px;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            flex: 1 0 0;
          `}
        >
          <TextInput
            ref={websiteRef}
            label='Website'
            value={state.website}
            setValue={(value) => {
              dispatch({ type: 'SET_FIELD', field: 'website', value })
            }}
            onBlur={() => {
              if (
                state.website.length > 0 &&
                !state.website.match(websiteRegex)
              ) {
                setCustomErrorByField('website', 'Invalid website')
              } else {
                clearCustomErrorByField('website')
              }
            }}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            name='website'
            type='url'
          />
        </div>
      </div>

      {/* Separator */}
      <div
        css={css`
          height: 1px;
          width: 100%;
          background-color: ${colors.SECONDARY_200};
        `}
      />

      {/* Support Info Header */}
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <Text variant='captionLarge' weight='semiBold'>
          Support Info
        </Text>
      </div>

      {/* Support Phone + Support Email row */}
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          align-self: stretch;
          flex-direction: row;
          gap: 16px;
          ${isMobile &&
          css`
            flex-direction: column;
          `}
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            flex: 1 0 0;
            ${isMobile &&
            css`
              width: 100%;
            `}
          `}
        >
          <ModernPhoneInput
            ref={supportPhoneRef}
            value={state.support_phone}
            onChange={(value) => {
              dispatch({ type: 'SET_FIELD', field: 'support_phone', value })
            }}
            onCountryChange={(country) => {
              dispatch({
                type: 'SET_FIELD',
                field: 'support_phone_country',
                value: country || '',
              })
            }}
            label='Support Phone Number'
            error={
              state.fieldErrors.includes('support_phone')
                ? 'Invalid phone number'
                : ''
            }
            validateOnBlur={false}
          />
        </div>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            flex: 1 0 0;
            ${isMobile &&
            css`
              width: 100%;
            `}
          `}
        >
          <TextInput
            ref={supportEmailRef}
            label='Support Email'
            value={state.support_email}
            setValue={(value) => {
              dispatch({ type: 'SET_FIELD', field: 'support_email', value })
            }}
            onBlur={() => {
              if (
                state.support_email.length > 0 &&
                !emailRegex.test(state.support_email)
              ) {
                setCustomErrorByField('support_email', 'Invalid support email')
              } else {
                clearCustomErrorByField('support_email')
              }
            }}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            name='support_email'
            type='email'
          />
        </div>
      </div>

      {/* Support Website */}
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          align-self: stretch;
          flex-direction: row;
          gap: 16px;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            flex: 1 0 0;
          `}
        >
          <TextInput
            ref={supportWebsiteRef}
            label='Support Website'
            value={state.support_website}
            setValue={(value) => {
              dispatch({ type: 'SET_FIELD', field: 'support_website', value })
            }}
            onBlur={() => {
              if (
                state.support_website.length > 0 &&
                !state.support_website.match(websiteRegex)
              ) {
                setCustomErrorByField(
                  'support_website',
                  'Invalid support website'
                )
              } else {
                clearCustomErrorByField('support_website')
              }
            }}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            name='support_website'
            type='url'
          />
        </div>
      </div>
    </div>
  )
}

export default ContactInfo
